import styled from 'styled-components'
import { t, jt } from 'ttag'
import { useState, useCallback } from 'react'

import navigatorAccountSvg from '@h2/assets/svg/illustrations/navigatorAccount.svg'
import exploreSvg from '@h2/assets/svg/illustrations/explore.svg'
import { features } from '@h2/components/media'
import { vagRundFont } from '@h2/components/mixins'
import { PG, PG90T, PG80T, SLB90T, SLB80T, SLB } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Option, OptionGroup } from '@h2/components/containers/optionGroup'
import { Card } from '@h2/common/components/card'

import { props } from './props'
import { OptionBody } from './components'

import { Create } from '../highlights/create'
import { Back } from '../back'
import { Controls } from '../controls'
import { nevadaSpecific } from '../../../utils/nevada'

const color = {
  explorer: PG,
  navigator: SLB
}
const backgroundColor = {
  explorer: PG90T,
  navigator: SLB90T,
  hover: {
    explorer: PG80T,
    navigator: SLB80T
  }
}
const students = <strong key="jts">{t`Students`}</strong>
const jobseekers = <strong key="jtj">{t`Jobseekers`}</strong>
const educators = <strong key="jte">{t`Educators`}</strong>
const counselors = <strong key="jtc">{t`Career Counselors`}</strong>
const family = <strong key="jtf">{t`Family Members`}</strong>
const AccountOption = styled(Option)`
  background-color: ${({ value }) => backgroundColor[value]};

  > div {
    padding: 20px;
    gap: 25px;

    > img {
      height: auto;
      width: 105px;
      align-self: auto;
    }

    > span.flex {
      ${vagRundFont};

      > span:first-child {
        font-size: 24px;
        font-weight: 400;
        letter-spacing: 0.8px;
        color: ${({ value }) => color[value]};
      }

      > span:last-child {
        font-size: 16px;
        font-weight: 300;
        color: black;

        strong {
          font-weight: 400;
        }
      }
    }

    @media (${features.minWidth.laptop}) {
      gap: 5px;

      > img {
        height: 72px;
        width: auto;
      }

      > span.flex {
        > span:last-child {
          margin-bottom: auto;
        }
      }
    }
  }

  &:hover,
  &.selected {
    outline-width: 3px;
    background-color: ${({ value }) => backgroundColor.hover[value]};
  }
`
const Wrap = styled(Flex)`
  height: 100%;
  flex-direction: column;

  > ul {
    flex-direction: column;
  }

  @media (${features.minWidth.laptop}) {
    > ul {
      flex-direction: row;

      > li {
        min-height: 215px;
      }
    }
  }
`
const resets = {
  navigatorType: null,
  counselorType: null,
  educatorType: null,
  k12EducatorType: null,
  k12Population: null,
  k12Program: null,
  isStudent: null,
  studentType: null,
  graduationYear: null
}
const AccountType = ({ inDialog, validate, renderPanel }) => {
  const [selected, setSelected] = useState('')
  const handleOnClick = useCallback(
    value => {
      setSelected(value)
      validate({ isExplorer: value === 'explorer', ...resets })
    },
    [validate]
  )

  return (
    <Card inDialog={inDialog}>
      <Card.Highlight color={PG}>
        <Create />
      </Card.Highlight>
      <Card.Content>
        <Wrap>
          <Heading level="2" align="center" mb="12px">
            {t`Which account type will you need?`}
          </Heading>
          <Text align="center">
            {t`Choose the account type that best describes how you plan to use the Career Explorer, so we can customize your account with the tools you need.`}
          </Text>
          <OptionGroup gap="15px" onClick={handleOnClick}>
            <AccountOption
              value="explorer"
              className={selected === 'explorer' ? 'selected' : null}>
              <OptionBody>
                <img src={exploreSvg} alt="" />
                <span className="flex">
                  <span>{t`Explorer`}</span>
                  <span>{jt`${students} or ${jobseekers} exploring and planning career opportunities`}</span>
                </span>
              </OptionBody>
            </AccountOption>
            <AccountOption
              value="navigator"
              className={selected === 'navigator' ? 'selected' : null}>
              <OptionBody>
                <img src={navigatorAccountSvg} alt="" />
                <span className="flex">
                  <span>{t`Navigator`}</span>
                  <span>
                    {nevadaSpecific(
                      <strong>{t`Librarians, One-Stop Coaches, Community College Advisors, State and Municipal Agencies, Non-Government Organizations, and Educators working with Explorers`}</strong>,
                      jt`${educators}, ${counselors}, or ${family} working with Explorers on their journey`
                    )}
                  </span>
                </span>
              </OptionBody>
            </AccountOption>
          </OptionGroup>
          <Controls>
            <Back onClick={() => renderPanel('email')} />
          </Controls>
        </Wrap>
      </Card.Content>
      <Card.Detour>
        <Back onClick={() => renderPanel('email')} />
      </Card.Detour>
    </Card>
  )
}

AccountType.propTypes = { ...props }

export { AccountType }
